import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  ASSET_CONDITIONS,
  assetsApi,
  type AssetCondition,
  type AssetDetail,
  type AssetSummary,
} from "../api/assets";
import { locationsApi } from "../api/locations";
import type { ApiError } from "../api/client";
import { formatApiError } from "../lib/apiError";
import { localDateTimeInputToIso, toLocalDateTimeInput } from "../lib/dates";
import { flattenLocations, type FlatLocation } from "../lib/locationsFlat";
import { ConditionChip, conditionLabel } from "./ConditionChip";
import { DateTimeInput } from "./DateInput";
import { LocationDestinationFields, missingReturnDate, returnDateFor } from "./LocationDestinationFields";
import { Modal } from "./Modal";
import { Select, type SelectOption } from "./Select";

/** Small event dialogs of the asset karton: move (unmounted), condition change, free note. */

export function MoveAssetModal({ asset, onClose, onDone }: { asset: AssetSummary; onClose: () => void; onDone: (d: AssetDetail) => void }) {
  const { t } = useTranslation();
  const [flat, setFlat] = useState<FlatLocation[] | null>(null);
  const [locationId, setLocationId] = useState("");
  const [locationNote, setLocationNote] = useState(asset.locationNote ?? "");
  const [expectedReturnAt, setExpectedReturnAt] = useState("");
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeInput());
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    locationsApi.tree()
      .then((tree) => { if (!cancelled) setFlat(flattenLocations(tree)); })
      .catch((err: ApiError) => { if (!cancelled) { setFlat([]); setError(err.message); } });
    return () => { cancelled = true; };
  }, []);

  const same = !!locationId && locationId === asset.effectiveLocation?.id;
  const canSubmit = !!locationId && !missingReturnDate(flat, locationId, expectedReturnAt) && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      onDone(await assetsApi.move(asset.id, {
        toLocationId: locationId,
        locationNote: locationNote.trim() || null,
        expectedReturnAt: returnDateFor(flat, locationId, expectedReturnAt),
        occurredAt: localDateTimeInputToIso(occurredAt),
        note: note.trim() || null,
      }));
    } catch (err) {
      const ae = err as ApiError;
      setError(ae.code === "MOUNTED" ? t("assets.error.mountedMove", "Montiran asset se ne premešta — demontirajte ga.") : formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t("move.title", "Premeštanje asseta")}
      subtitle={t("move.sub", "{{tag}} — trenutno: {{loc}}", { tag: asset.tag, loc: asset.effectiveLocation?.name ?? "—" })}
      onClose={onClose}
      width={600}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <LocationDestinationFields
          flat={flat}
          label={t("move.toLocation", "Nova fizička lokacija")}
          locationId={locationId}
          onLocationChange={setLocationId}
          locationNote={locationNote}
          onLocationNoteChange={setLocationNote}
          expectedReturnAt={expectedReturnAt}
          onExpectedReturnAtChange={setExpectedReturnAt}
        />
        {same && <div className="muted" style={{ fontSize: 12.5 }}>{t("move.sameLocation", "To je već trenutna lokacija (menja se samo napomena).")}</div>}
        <div className="form-grid">
          <label className="field">
            <span>{t("assets.field.occurredAt", "Datum i vreme")}</span>
            <DateTimeInput value={occurredAt} max={toLocalDateTimeInput()} onChange={setOccurredAt} />
          </label>
        </div>
        <label className="field">
          <span>{t("assets.field.note", "Napomena")}</span>
          <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn lg" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary lg" disabled={!canSubmit}>
            {submitting ? t("action.saving", "Čuvanje…") : t("move.submit", "Premesti")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function ConditionModal({ asset, onClose, onDone }: { asset: AssetSummary; onClose: () => void; onDone: (d: AssetDetail) => void }) {
  const { t } = useTranslation();
  const [condition, setCondition] = useState<AssetCondition>(asset.condition);
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeInput());
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mounted = asset.usage === "MONTIRAN";
  const options = useMemo<SelectOption<AssetCondition>[]>(
    () => ASSET_CONDITIONS.map((c) => ({ value: c, label: conditionLabel(t, c) })),
    [t],
  );
  const unchanged = condition === asset.condition;
  const writeOffMounted = mounted && condition === "OTPISAN";
  const canSubmit = !unchanged && !writeOffMounted && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      onDone(await assetsApi.condition(asset.id, { condition, occurredAt: localDateTimeInputToIso(occurredAt), note: note.trim() || null }));
    } catch (err) {
      const ae = err as ApiError;
      setError(ae.code === "MOUNTED" ? t("assets.error.writeOffMounted", "Montiran asset se ne otpisuje — prvo ga demontirajte.") : formatApiError(ae));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t("condition.title", "Promena stanja")} subtitle={asset.tag} onClose={onClose} width={520}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div className="field">
          <span>{t("condition.current", "Trenutno stanje")}</span>
          <div><ConditionChip condition={asset.condition} /></div>
        </div>
        <label className="field">
          <span>{t("condition.new", "Novo stanje")} <span className="req-mark">*</span></span>
          <Select<AssetCondition> value={condition} options={options} onChange={setCondition} ariaLabel={t("condition.new", "Novo stanje")} />
        </label>
        {writeOffMounted && <div style={noticeBox}>{t("assets.error.writeOffMounted", "Montiran asset se ne otpisuje — prvo ga demontirajte.")}</div>}
        <div className="form-grid">
          <label className="field">
            <span>{t("assets.field.occurredAt", "Datum i vreme")}</span>
            <DateTimeInput value={occurredAt} max={toLocalDateTimeInput()} onChange={setOccurredAt} />
          </label>
        </div>
        <label className="field">
          <span>{t("assets.field.note", "Napomena")}</span>
          <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} />
        </label>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn lg" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary lg" disabled={!canSubmit}>
            {submitting ? t("action.saving", "Čuvanje…") : t("condition.submit", "Promeni stanje")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export function NoteEventModal({ asset, onClose, onDone }: { asset: AssetSummary; onClose: () => void; onDone: (d: AssetDetail) => void }) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [occurredAt, setOccurredAt] = useState(() => toLocalDateTimeInput());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSubmit = note.trim().length > 0 && !submitting;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      onDone(await assetsApi.note(asset.id, { note: note.trim(), occurredAt: localDateTimeInputToIso(occurredAt) }));
    } catch (err) {
      setError(formatApiError(err as ApiError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={t("noteEvent.title", "Novi zapis")} subtitle={t("noteEvent.sub", "{{tag}} — beleška u istoriji komada", { tag: asset.tag })} onClose={onClose} width={520}>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label className="field">
          <span>{t("noteEvent.note", "Zapis")} <span className="req-mark">*</span></span>
          <textarea className="input" rows={4} value={note} onChange={(e) => setNote(e.target.value)} style={textarea} required placeholder={t("noteEvent.placeholder", "npr. Izbušena rupa fi 12 na prirubnici")} />
        </label>
        <div className="form-grid">
          <label className="field">
            <span>{t("assets.field.occurredAt", "Datum i vreme")}</span>
            <DateTimeInput value={occurredAt} max={toLocalDateTimeInput()} onChange={setOccurredAt} />
          </label>
        </div>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn lg" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary lg" disabled={!canSubmit}>
            {submitting ? t("action.saving", "Čuvanje…") : t("noteEvent.submit", "Sačuvaj zapis")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const noticeBox: CSSProperties = { padding: 10, background: "rgba(185,122,14,.10)", color: "var(--warn)", borderRadius: 6, fontSize: 13 };
const textarea: CSSProperties = { resize: "vertical", fontFamily: "inherit", width: "100%" };
